import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../reduxStore';
import { hideLoader, showLoader } from './loaderSlice';
import { showErrorSnackbar, showSuccessSnackbar } from './snackbarSlice';
import {
  cancelProductionApi,
  getProductionReport,
  getProductionReportByIdApi
} from '../../ApiServices/ProductionReportHistoryServices';
import { handleResponse } from '../../ApiServices/ApiHandler';
import { t } from 'i18next';

const initialState = {
  value: { totalCount: -1, data: [{}] },
  productsValue: [],
  loading: false,
  error: null,
  selectedValue: null,
  rowsPerPage: 10,
  pageNo: 0,
  filter: {}
};

const productionReportSlice = createSlice({
  name: 'productionReport',
  initialState,
  reducers: {
    clearProductionReportState(state: any) {
      state.rowsPerPage = initialState.rowsPerPage;
      state.pageNo = initialState.pageNo;
      state.value = initialState.value;
      state.loading = false;
      state.error = null;
      state.selectedValue = initialState.selectedValue;
      state.filter = initialState.filter;
      state.productsValue = initialState.productsValue;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchProductionReport.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.value = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      state.rowsPerPage = action?.meta?.arg?.limit;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchProductionReport.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = false;
      state.selectedValue = initialState?.selectedValue;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(getProductionReportById.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.productsValue = action?.payload?.data;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(getProductionReportById.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.productsValue = initialState.productsValue;
      state.error = action?.payload;
      state.filter = action?.meta?.arg;
    });
  }
});

export const productionReportState = (state: RootState) => state?.productionReport;
export const { clearProductionReportState } = productionReportSlice.actions;
export default productionReportSlice.reducer;

export const fetchProductionReport = createAsyncThunk(
  'productionReport/fetchProductionReport',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_PRODUCTION_REPORT') }));
      return fulfillWithValue(await getProductionReport(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const getProductionReportById = createAsyncThunk(
  'productionReport/fetchProductionReportById',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('GETTING_REPORT') }));
      //return fulfillWithValue(await getProductionReportByIdApi(payload));
      let response: any = await getProductionReportByIdApi(payload);
      if (response?.status === 200 && response?.data?.length === 0) {
        dispatch(showErrorSnackbar({ message: t('NO_DATA_FOUND') }));
      }
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const cancelProduction = createAsyncThunk(
  'productionReport/cancelProduction',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('CANCELLING_PRODUCTION') }));
      let response = await cancelProductionApi(payload);
      if (response.status === 200) {
        dispatch(showSuccessSnackbar({ message: response?.data?.message }));
      }
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);
